import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function businessIdFromState(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

export const fetchNotificationPreferences = createAsyncThunk(
  "notificationPreferences/fetch",
  async (_, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/notification/${businessId}/preferences`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const updateNotificationPreferences = createAsyncThunk(
  "notificationPreferences/update",
  async (body, { getState, rejectWithValue }) => {
    const businessId = businessIdFromState(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.patch(
        `${ROOT_URL}/notification/${businessId}/preferences`,
        body,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  data: null,
  triggers: {},
  triggerLabels: {},
  soundOptions: [],
  soundEnabled: true,
  soundKey: "default",
  repeatSound: false,
  loading: false,
  loaded: false,
  saving: false,
  error: null,
};

function applyPreferencesData(state, payload) {
  const data = payload?.data ?? payload;
  if (!data) return;

  state.data = data;
  state.triggers = data.triggers ?? {};
  state.triggerLabels = data.triggerLabels ?? {};
  state.soundOptions = data.soundOptions ?? [];
  state.soundEnabled = data.soundEnabled ?? true;
  state.soundKey = data.soundKey ?? "default";
  state.repeatSound = data.repeatSound ?? false;
}

const notificationPreferencesApi = createSlice({
  name: "notificationPreferences",
  initialState,
  reducers: {
    clearNotificationPreferences: () => initialState,
    clearNotificationPreferencesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        applyPreferencesData(state, action.payload);
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload;
      })
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.saving = false;
        applyPreferencesData(state, action.payload);
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearNotificationPreferences, clearNotificationPreferencesError } =
  notificationPreferencesApi.actions;

export default notificationPreferencesApi.reducer;
